import React from 'react';
import { ScenarioOptions, EnhancedAssumptions } from '../../types';
import { HorizontalSelector } from '../ui';

interface ScenarioSelectorProps {
  label: string;
  scenarioType: string;
  currentScenario: string;
  currentValue: number;
  scenarioOptions: ScenarioOptions;
  onScenarioChange: (type: string, scenario: string) => void;
  onCustomValueChange: (field: keyof EnhancedAssumptions, value: string) => void;
}

export const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({
  label,
  scenarioType,
  currentScenario,
  currentValue,
  scenarioOptions,
  onScenarioChange,
  onCustomValueChange
}) => {
  const currentOption = scenarioOptions[currentScenario];

  // Create short labels for compact display
  const getShortLabel = (key: string, fullLabel: string) => {
    // Handle scenario-specific mappings
    const mappings: Record<string, Record<string, string>> = {
      propertyValue: {
        'conservative': 'Low',
        'current_estimate': 'Zillow',
        'redfin_estimate': 'Redfin', 
        'optimistic': 'High'
      },
      monthlyRent: {
        'conservative': 'Low',
        'zillow_estimate': 'Zillow',
        'redfin_estimate': 'Redfin',
        'premium': 'High'
      },
      propertyAppreciation: {
        'conservative': 'Low',
        'historical_longterm': 'Avg', 
        'historical_20yr': '20yr',
        'recent_strong': 'High',
        'optimistic': 'Max'
      },
      rentGrowth: {
        'inflation': 'None',
        'modest': 'Low',
        'strong': 'High', 
        'very_strong': 'Max'
      },
      stockReturns: {
        'conservative': 'Low',
        'historical_longterm': 'Avg',
        'historical_20yr': '20yr', 
        'recent_strong': 'High',
        'optimistic': 'Max'
      },
      bondReturns: {
        'low': 'Low',
        'current': 'Cur',
        'historical': 'Avg',
        'rising_rates': 'High'
      },
      propertyTax: {
        'low': 'Low',
        'typical': 'Avg',
        'high': 'High',
        'very_high': 'Max'
      },
      vacancyRate: {
        'excellent': 'Min',
        'good': 'Low', 
        'average': 'Avg',
        'challenging': 'High'
      },
      managementFee: {
        'self_managed': 'None',
        'low': 'Low',
        'standard': 'Avg',
        'premium': 'High'
      },
      maintenanceRate: {
        'new_property': 'Min',
        'well_maintained': 'Low',
        'average': 'Avg', 
        'older_property': 'High'
      },
      insuranceCost: {
        'low': 'Low',
        'typical': 'Avg',
        'high': 'High',
        'very_high': 'Max'
      },
      sellingCosts: {
        'low': 'Low',
        'typical': 'Avg', 
        'high': 'High',
        'very_high': 'Max'
      },
      rentalSetupCosts: {
        'minimal': 'Min',
        'standard': 'Avg',
        'moderate': 'Mod',
        'extensive': 'Max'
      }
    };
    
    const scenarioMappings = mappings[scenarioType];
    if (scenarioMappings && scenarioMappings[key]) {
      return scenarioMappings[key];
    }
    
    return fullLabel.split(' ')[0];
  };

  // Convert scenario options to HorizontalSelector format
  const selectorOptions = Object.entries(scenarioOptions).map(([key, option]) => {
    let valueLabel = `${option.value}%`;
    
    // Format value labels based on scenario type
    if (scenarioType === 'propertyValue') {
      valueLabel = `$${(option.value / 1000000).toFixed(1)}M`;
    } else if (scenarioType === 'monthlyRent') {
      valueLabel = `$${(option.value / 1000).toFixed(0)}K`;
    } else if (scenarioType === 'insuranceCost' || scenarioType === 'rentalSetupCosts') {
      valueLabel = `$${option.value.toLocaleString()}`;
    } else if (scenarioType === 'sellingCosts') {
      valueLabel = `${option.value}%`;
    }
    
    return {
      value: key,
      label: option.label,
      shortLabel: getShortLabel(key, option.label),
      valueLabel
    };
  });

  // Determine color scheme based on scenario type
  const getColorScheme = () => {
    if (scenarioType.includes('Return') || scenarioType.includes('Growth') || scenarioType.includes('Appreciation') || scenarioType.includes('stock') || scenarioType.includes('bond')) {
      return 'performance';
    } else if (scenarioType.includes('Tax') || scenarioType.includes('maintenance') || scenarioType.includes('vacancy') || scenarioType.includes('management') || scenarioType.includes('property') || scenarioType.includes('insurance') || scenarioType.includes('selling') || scenarioType.includes('setup')) {
      return 'risk';
    }
    return 'default';
  };

  return (
    <div>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      </div>
      
      <HorizontalSelector
        options={selectorOptions}
        value={currentScenario}
        onChange={(value) => onScenarioChange(scenarioType, value)}
        colorScheme={getColorScheme()}
        size="sm"
        className="mb-3"
      />

      {currentOption && (
        <div className="bg-gray-50 px-3 py-2  border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">{currentOption.description}</p>
          {currentOption.citation && (
            currentOption.link ? (
              <a
                href={currentOption.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-600 hover:text-gray-800 underline cursor-pointer"
              >
                <span className="font-medium">Source:</span> {currentOption.citation}
              </a>
            ) : (
              <span className="text-xs text-gray-500">
                <span className="font-medium">Source:</span> {currentOption.citation}
              </span>
            )
          )}
        </div>
      )}
    </div>
  );
};